<script setup lang="ts">
import { RULES } from "../../domain/rules.js";

const emit = defineEmits<{ back: [] }>();

/**
 * The rules, for people who have not played before.
 *
 * The numbers come from the rules engine (RULES), not from prose, so the page
 * cannot drift out of step with how the app actually scores. The dice-combination
 * table is reference only -- the app never computes it, players work it out at
 * the table -- but newcomers need it to understand what they are entering.
 */

const combos = [
  { combo: "Eine 1", points: "100" },
  { combo: "Eine 5", points: "50" },
  { combo: "Drei 1er in einem Wurf", points: "1.000" },
  { combo: "Drilling (2–6)", points: "Augenzahl × 100" },
  { combo: "Straße 1-2-3-4-5-6", points: "2.000" },
  { combo: "Drei Paare", points: "1.500" },
];
</script>

<template>
  <div class="screen">
    <header class="nav">
      <button
        class="nav-btn"
        @click="emit('back')"
      >
        ‹ Zurück
      </button>
      <span class="title">Spielregeln</span>
      <span class="spacer" />
    </header>

    <div class="scroll">
      <p class="lead">
        Dänisch Kniffel wird mit sechs Würfeln gespielt. Ziel ist es, als
        Erste:r <strong>{{ RULES.target.toLocaleString("de-DE") }} Punkte</strong>
        zu erreichen. Gewürfelt wird am Tisch – die App zählt nur mit.
      </p>

      <section class="block">
        <h2>Ein Zug</h2>
        <p>
          Wer dran ist, würfelt und sammelt Punkte. Ein Zug endet auf genau eine
          von zwei Arten:
        </p>
        <div class="two">
          <div class="card good">
            <div class="card-h">
              Eintragen
            </div>
            <p>
              Mit mindestens
              <strong>{{ RULES.minBank }} Punkten</strong> aufhören. Der Wert
              kommt aufs Konto, die Strich-Serie wird auf null gesetzt. Jeder
              Wert ist ein Vielfaches von {{ RULES.granularity }}.
            </p>
          </div>
          <div class="card bad">
            <div class="card-h">
              Strich
            </div>
            <p>
              Kein zählender Wurf oder unter {{ RULES.minBank }} geblieben. Der
              Zug bringt <strong>0 Punkte</strong> und die Strich-Serie wächst um
              eins.
            </p>
          </div>
        </div>
      </section>

      <section class="block">
        <h2>Die Strafe</h2>
        <p>
          Drei Striche <strong>hintereinander</strong> kosten
          <strong class="danger">−{{ RULES.penalty.toLocaleString("de-DE") }} Punkte</strong>,
          dann beginnt die Zählung von vorn. Der vierte Strich in Folge kostet
          also zunächst nichts wieder.
        </p>
        <p class="aside">
          Konten dürfen ins Minus rutschen – ein Punkteboden existiert nicht.
        </p>
      </section>

      <section class="block">
        <h2>Gewinnen</h2>
        <ul class="list">
          <li>
            Wer <strong>{{ RULES.target.toLocaleString("de-DE") }} oder mehr</strong>
            erreicht, löst das Spielende aus – genau
            {{ RULES.target.toLocaleString("de-DE") }} sind nicht nötig.
          </li>
          <li>
            Die <strong>laufende Runde wird zu Ende gespielt</strong>: alle, die
            noch nicht dran waren, bekommen ihren letzten Zug.
          </li>
          <li>
            Es gewinnt, wer danach <strong>am meisten Punkte</strong> hat – nicht
            zwingend die Person, die zuerst über die Ziellinie kam.
          </li>
        </ul>
      </section>

      <section class="block">
        <h2>Würfelkombinationen</h2>
        <p class="aside">
          Zur Orientierung – die App rechnet das nicht aus, ihr zählt am Tisch.
        </p>
        <table class="combos">
          <tbody>
            <tr
              v-for="c in combos"
              :key="c.combo"
            >
              <td>{{ c.combo }}</td>
              <td class="pts num">
                {{ c.points }}
              </td>
            </tr>
          </tbody>
        </table>
        <p class="aside">
          Zählen alle sechs Würfel, darf mit allen sechs weitergewürfelt werden.
          Andere Kombinationen zählen nicht.
        </p>
      </section>
    </div>
  </div>
</template>

<style scoped>
.screen {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-left: max(16px, var(--safe-left));
  padding-right: max(16px, var(--safe-right));
  padding-bottom: calc(var(--safe-bottom) + 8px);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: calc(var(--safe-top) + 6px) 0 6px;
  min-height: 52px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 15px;
  padding: 8px 4px;
  min-height: 44px;
}

.title {
  font-size: 15px;
  font-weight: 600;
}

.spacer {
  width: 56px;
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.lead {
  font-size: 16px;
  line-height: 1.5;
  color: var(--text);
  margin: 4px 2px 20px;
}

.block {
  margin-bottom: 22px;
}

.block h2 {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-2);
  margin: 0 2px 8px;
}

.block p {
  font-size: 15px;
  line-height: 1.5;
  margin: 0 2px 8px;
}

.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.card {
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 12px;
}

.card p {
  margin: 0;
  font-size: 13px;
  color: var(--text-2);
}

.card-h {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px;
}

.card.good .card-h {
  color: var(--accent);
}

.card.bad .card-h {
  color: var(--danger);
}

.danger {
  color: var(--danger);
}

.aside {
  font-size: 13px;
  color: var(--text-2);
}

.list {
  margin: 0;
  padding-left: 20px;
}

.list li {
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 6px;
}

.combos {
  width: 100%;
  border-collapse: collapse;
  background: var(--card);
  border-radius: var(--radius-card);
  overflow: hidden;
  margin: 8px 0;
}

.combos td {
  padding: 10px 12px;
  font-size: 14px;
}

.combos tr + tr td {
  border-top: 1px solid var(--separator);
}

.pts {
  text-align: right;
  font-weight: 600;
  color: var(--accent);
  white-space: nowrap;
}
</style>
